import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Trash2 } from "lucide-react";
import Loading from "../Common/Loading";
import ConfirmDialog from "../Common/ConfirmDialog";
import SideModal from "./SideModal";
import AddBulkicon from "../assets/AddBulkicon.png";
import { API } from "../Common/Api";
import { ACTIONS } from "../redux/actions";
import { ErrorToast, SuccessToast } from "../Utils/toast";

// An agent can only be deleted once nothing is assigned to or activated under
// them. The backend enforces the same rule — this just keeps the icon hidden
// until it can succeed.
const hasNoInventory = (user) => !(user.assignedCount > 0) && !(user.activatedCount > 0);

const DistributorAgentsModal = ({ distributor, onClose, setModalOpen }) => {
  const dispatch = useDispatch();
  const { GetAgentsDistributorWiseData, GetAgentsDistributorWiseLoading: loading } = useSelector((state) => state.GetAgentsDistributorWise);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(false);

  // Since we filter by distributorId, the first element in data.data contains our distributor and its agents
  const distributors = GetAgentsDistributorWiseData?.data?.data || [];
  const agents = distributors[0]?.agents || [];

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await API.DeleteDealerOrAgent(deleteTarget.id);
      if (res?.status === 200 && res?.success) {
        SuccessToast(res?.message || "Agent deleted permanently");
        // Re-fetch this distributor's agents so the row disappears.
        dispatch(ACTIONS.GetAgentsDistributorWiseAction({ distributorId: distributor?._id }));
      } else {
        ErrorToast(res?.message || "Failed to delete agent");
      }
    } catch (e) {
      ErrorToast("Failed to delete agent");
    } finally {
      setDeleteTarget(null);
      setDeleting(false);
    }
  };

  return (
    <SideModal
      title={`Agents for ${distributor?.username}`}
      image={AddBulkicon}
      onClose={onClose}
      setModalOpen={setModalOpen}
      width="750px"
    >
      <div className="mt-4 overflow-x-auto relative min-h-[300px]">
        <Loading loading={loading} overlay="table" />
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Agent Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Mobile Number
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Email
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Assigned
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Activated
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Delete
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {agents.length > 0 ? (
              agents.map((agent) => (
                <tr key={agent._id}>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {agent.username}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {agent.mobileNo}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {agent.email || "-"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-500">
                    {agent.assignedCount || 0}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-500">
                    {agent.activatedCount || 0}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-center">
                    {hasNoInventory(agent) && (
                      <button
                        onClick={() => setDeleteTarget({ id: agent._id, name: agent.username })}
                        title="Delete agent"
                        aria-label={`Delete agent ${agent.username}`}
                        className="inline-flex items-center justify-center p-1.5 rounded-md text-gray-400 hover:text-[#EE3B31] hover:bg-rose-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              !loading && (
                <tr>
                  <td colSpan="6" className="px-4 py-4 text-center text-sm text-gray-500">
                    No agents found for this distributor
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>

      {/* Portaled to <body>: SideModal's slide-in transform would otherwise make
          this fixed overlay relative to the panel instead of the screen. */}
      {createPortal(
        <ConfirmDialog
          open={!!deleteTarget}
          title="Delete agent permanently?"
          message={`"${deleteTarget?.name}" will be permanently deleted. This cannot be undone — after deletion this user can't be activated or sign in again.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          loading={deleting}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />,
        document.body
      )}
    </SideModal>
  );
};

export default DistributorAgentsModal;
